/**
 * Image utilities to optimize Supabase storage usage and reduce egress
 */

/**
 * Get a resized image URL from Supabase storage
 * Uses the Supabase image transformation API to reduce bandwidth
 * 
 * @param url The original Supabase storage URL
 * @param width Desired width (if null, no width resizing)
 * @param height Desired height (if null, no height resizing)
 * @param format Image format ('webp' is recommended for best compression)
 * @param quality Image quality (0-100, lower means smaller file size)
 * @returns URL with transformation parameters
 */
export function getOptimizedImageUrl(
  url: string,
  options: {
    width?: number;
    height?: number;
    format?: 'webp' | 'jpg' | 'png' | 'auto';
    quality?: number;
  } = {}
): string {
  if (!url) return '';
  
  // Skip optimization for non-Supabase URLs or SVGs (which are already optimized)
  if (!url.includes('supabase.co/storage/v1/object') || url.endsWith('.svg')) {
    return url;
  }
  
  // Check for existing transformation parameters
  if (url.includes('?')) {
    // URL already has query parameters, skip optimization
    return url;
  }
  
  // Default options
  const { 
    width = null, 
    height = null, 
    format = 'webp', 
    quality = 80
  } = options;
  
  // Start building transformation query
  const params = new URLSearchParams();
  
  // Add width if specified
  if (width) {
    params.append('width', width.toString());
  }
  
  // Add height if specified
  if (height) {
    params.append('height', height.toString());
  }
  
  // Add format unless it's 'auto'
  if (format !== 'auto') {
    params.append('format', format);
  }
  
  // Add quality
  params.append('quality', quality.toString());
  
  // Return URL with transformation parameters
  return `${url}?${params.toString()}`;
}

/**
 * Get appropriate image size based on viewport or container size
 * This helps in serving the right sized images for different devices
 * 
 * @param containerWidth Width of the container or viewport
 * @returns Optimal image width
 */
export function getOptimalImageSize(containerWidth: number): number {
  // For very small containers (mobile)
  if (containerWidth <= 480) {
    return 480;
  }
  // For medium containers (tablets)
  else if (containerWidth <= 768) {
    return 768;
  }
  // For large containers (desktop)
  else if (containerWidth <= 1200) {
    return 1200;
  }
  // For very large displays
  else {
    return 1600;
  }
}

/**
 * Process image before upload to reduce its size
 * 
 * @param file The image file to process
 * @param maxWidth Maximum width of the processed image
 * @param maxHeight Maximum height of the processed image
 * @param quality Quality of the processed image (0-1)
 * @returns Promise with the processed file
 */
export function processImageForUpload(
  file: File,
  maxWidth = 1600,
  maxHeight = 1600,
  quality = 0.8
): Promise<File> {
  return new Promise((resolve, reject) => {
    // Skip processing for non-image files or SVGs
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      resolve(file);
      return;
    }
    
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to WebP if supported (better compression)
        const fileType = 'image/webp';
        
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not create image blob'));
              return;
            }
            
            // Create new file with the processed image
            const newFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, '.webp'),
              {
                type: fileType,
                lastModified: file.lastModified,
              }
            );
            
            resolve(newFile);
          },
          fileType,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Could not load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Could not read file'));
    };
  });
}

export function generateBlurDataUrl(width: number, height: number): string {
  return `data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'%3E%3Cfilter id='b' color-interpolation-filters='sRGB'%3E%3CfeGaussianBlur stdDeviation='20'/%3E%3CfeColorMatrix values='1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 100 -1' result='s'/%3E%3CfeFlood x='0' y='0' width='100%25' height='100%25'/%3E%3CfeComposite operator='out' in='s'/%3E%3C/filter%3E%3Cimage width='100%25' height='100%25' x='0' y='0' preserveAspectRatio='none' style='filter: url(%23b);' href='data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'/%3E%3C/svg%3E`;
}

// Constants for image optimization
export const IMAGE_SIZES = {
  thumbnail: { width: 100, height: 100 },
  small: { width: 300, height: 300 },
  medium: { width: 600, height: 600 },
  large: { width: 1200, height: 1200 }
} as const;

export const DEFAULT_IMAGE_QUALITY = 80; 